import * as React from 'react';
import { authService, Analytics } from '@withjoy/web-core';
import { handlers } from './handlers';
import { AuthField, AuthType } from './types';

export interface AuthenticateState {
  email: AuthField;
  password: AuthField;
  loginError: string;
  signupError: string;
  redirectUri: string;
}
export type CurrentForm = 'login' | 'signup' | 'forgotPassword';

interface AuthenticateProps {
  loginAnalytics: Analytics;
  signUpAnalytics: Analytics;
  currentForm: CurrentForm;
  children: (...args: any[]) => React.ReactNode;
}

export class Authenticate extends React.Component<AuthenticateProps, AuthenticateState> {
  readonly state: AuthenticateState = {
    email: {
      value: '',
      error: false,
      errorText: ''
    },
    password: {
      value: '',
      error: false,
      errorText: ''
    },
    loginError: '',
    signupError: '',
    redirectUri: '/createwedding/tying-the-knot'
  };

  handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    const field = e.currentTarget.dataset.input;
    this.setState(handlers.handleFormInput(field, value));
  };

  handleSignup = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { email, password, redirectUri } = this.state;
    this.props.signUpAnalytics.track({
      action: 'CreateAccount',
      actionType: 'click',
      label: 'create account'
    });
    try {
      const res = await authService.signupWithUsernameAndPassword(email.value, password.value, redirectUri);
      this.setState(handlers.handleSubmit());
      return res;
    } catch (e) {
      this.setState(handlers.handleError('signupError', e));
      this.props.signUpAnalytics.trackError({
        error: 'CreateWeddingValidation',
        attemptedAction: 'create account',
        errorDescription: e.message
      });
      return;
    }
  };

  handleInputErrorTracking = (authType: AuthType) => {
    const trackers = {
      login: {
        trackingFunction: this.props.loginAnalytics.trackError,
        attemptedAction: 'login'
      },
      signup: {
        trackingFunction: this.props.signUpAnalytics.trackError,
        attemptedAction: 'create account'
      }
    };
    const { email, password } = this.state;
    [email, password].forEach((field: AuthField) => {
      if (field.error) {
        const tracker = trackers[authType];
        tracker.trackingFunction({
          errorDescription: field.errorText,
          error: 'CreateWeddingValidation',
          attemptedAction: tracker.attemptedAction
        });
      }
    });
  };

  handleInputErrors = (email: AuthField, password: AuthField, authType: AuthType) => {
    this.setState(handlers.handleInputErrors(email, password, authType), () => this.handleInputErrorTracking(authType));
  };

  handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { email, password, redirectUri } = this.state;
    try {
      const res = await authService.loginWithUsernameAndPassword(email.value, password.value, redirectUri, '/');
      this.setState(handlers.handleSubmit());
      return res;
    } catch (e) {
      this.setState(handlers.handleError('loginError', e));
      this.props.signUpAnalytics.trackError({
        error: 'CreateWeddingValidation',
        attemptedAction: 'login',
        errorDescription: e.message
      });
      return;
    }
  };

  handleOAuth = (e: React.ChangeEvent<HTMLInputElement>) => {
    const provider = e.currentTarget.dataset.provider;
    const { currentForm, loginAnalytics, signUpAnalytics } = this.props;
    if (currentForm === 'login') {
      const action = provider === 'google' ? 'GoogleLogin' : 'FacebookLogin';
      loginAnalytics.track({ action, actionType: 'click' });
    } else if (currentForm === 'signup') {
      // should never be set to GoogleSignup bc we don't currently habve a button
      const action = provider === 'google' ? 'GoogleSignup' : 'FacebookSignup';
      signUpAnalytics.track({ action, actionType: 'click' });
    }
    if (provider) {
      const authenticate = {
        google: authService.loginWithGoogle,
        facebook: authService.loginWithFacebook
      };
      authenticate[provider]('/createwedding/tying-the-knot', '/createwedding');
    } else {
      // TODO: this would happen if the dataset was not there but not sure how to handle that
      console.log('provider not provided');
    }
  };

  render() {
    const { email, password, loginError, signupError } = this.state;
    return this.props.children(
      email,
      password,
      this.handleOnChange,
      this.handleSignup,
      this.handleLogin,
      this.handleOAuth,
      this.handleInputErrors,
      loginError,
      signupError
    );
  }
}
